import * as yup from "yup";

// Validation schema for creating an Asaas subaccount (admin)
// Mirrors AsaasSubaccountCreatePayload structure
export const asaasSubaccountCreateSchema = yup.object({
  establishment_id: yup
    .number()
    .typeError("Selecione um estabelecimento")
    .required("Estabelecimento é obrigatório"),
  accountType: yup
    .mixed<"PF" | "PJ">()
    .oneOf(["PF", "PJ"] as const, "Selecione o tipo de conta")
    .required("Selecione o tipo de conta"),
  name: yup
    .string()
    .trim()
    .required("Nome é obrigatório")
    .min(3, "Nome muito curto"),
  cpfCnpj: yup
    .string()
    .required("CPF/CNPJ é obrigatório")
    .transform((v) => (v ? v.replace(/\D/g, "") : v))
    .test("document-length", function (value) {
      const normalized = value ?? "";
      const accountType = (this.parent?.accountType ?? "PF") as "PF" | "PJ";

      if (accountType === "PJ") {
        if (normalized.length === 14) {
          return true;
        }
        return this.createError({ message: "CNPJ inválido" });
      }

      if (normalized.length === 11) {
        return true;
      }

      return this.createError({ message: "CPF inválido" });
    }),
  // birthDate é obrigatório somente para CPF
  birthDate: yup
    .string()
    .transform((v) => (v ? v.trim() : v))
    .when("accountType", (accountType: "PF" | "PJ", schema: any) =>
      accountType === "PF"
        ? schema
            .required("Data de nascimento é obrigatória para CPF")
            .matches(/^\d{4}-\d{2}-\d{2}$/, "Use o formato YYYY-MM-DD")
        : schema.notRequired(),
    ),
  // Emails
  email: yup.string().email("Email inválido").required("Email é obrigatório"),
  loginEmail: yup.string().email("Email de login inválido").notRequired(),
  mobilePhone: yup
    .string()
    .transform((v) => (v ? v.replace(/\D/g, "") : v))
    .required("Celular é obrigatório")
    .test(
      "len",
      "Celular inválido",
      (v) => !!v && (v.length === 10 || v.length === 11),
    ),
  incomeValue: yup
    .number()
    .typeError("Renda mensal é obrigatória")
    .required("Renda mensal é obrigatória")
    .min(0, "Renda deve ser positiva"),
  address: yup
    .object({
      street: yup.string().required("Rua obrigatória"),
      streetNumber: yup.string().required("Número obrigatório"),
      district: yup.string().required("Bairro obrigatório"),
      city: yup.string().required("Cidade obrigatória"),
      state: yup
        .string()
        .uppercase()
        .required("UF obrigatória")
        .test("uf", "UF inválida", (v) => !!v && v.length === 2),
      postalCode: yup
        .string()
        .transform((v) => (v ? v.replace(/\D/g, "") : v))
        .required("CEP obrigatório")
        .test("cep", "CEP inválido", (v) => !!v && v.length === 8),
    })
    .required("Endereço é obrigatório"),
  companyType: yup
    .mixed<"MEI" | "LIMITED" | "INDIVIDUAL" | "ASSOCIATION">()
    .oneOf(["MEI", "LIMITED", "INDIVIDUAL", "ASSOCIATION"] as const)
    .transform((value) => (value === "" ? undefined : value))
    .when("accountType", (accountType: "PF" | "PJ", schema: any) =>
      accountType === "PJ"
        ? schema.required("Tipo de empresa é obrigatório para CNPJ")
        : schema.notRequired(),
    ),
});

export type AsaasSubaccountCreateForm = yup.InferType<
  typeof asaasSubaccountCreateSchema
>;
